/**
 * Utility for checking application version and detecting updates
 */

const VERSION_CHECK_INTERVAL = 60000 // Check every minute
const VERSION_FILE_PATH = '/version.json'

let versionCheckInterval = null

/**
 * Get current version from localStorage
 */
export function getStoredVersion() {
  try {
    return localStorage.getItem('app_version') || '0.0.0'
  } catch (e) {
    return '0.0.0'
  }
}

/**
 * Store version in localStorage
 */
export function storeVersion(version) {
  try {
    localStorage.setItem('app_version', version)
  } catch (e) {
    console.error('Failed to store version:', e)
  }
}

/**
 * Fetch version from server
 */
export async function fetchServerVersion() {
  try {
    const timestamp = new Date().getTime()
    const response = await fetch(`${VERSION_FILE_PATH}?t=${timestamp}`)
    if (!response.ok) {
      throw new Error('Failed to fetch version')
    }
    const data = await response.json()
    return data.version || '0.0.0'
  } catch (e) {
    console.error('Error fetching version:', e)
    return null
  }
}

/**
 * Compare two version strings
 * Returns: 1 if v1 > v2, -1 if v1 < v2, 0 if equal
 */
export function compareVersions(v1, v2) {
  const parts1 = v1.split('.').map(Number)
  const parts2 = v2.split('.').map(Number)
  
  const maxLength = Math.max(parts1.length, parts2.length)
  
  for (let i = 0; i < maxLength; i++) {
    const part1 = parts1[i] || 0
    const part2 = parts2[i] || 0
    
    if (part1 > part2) return 1
    if (part1 < part2) return -1
  }
  
  return 0
}

/**
 * Check if update is available
 */
export async function checkForUpdate() {
  const serverVersion = await fetchServerVersion()
  if (!serverVersion) {
    console.warn('Failed to fetch server version')
    return false
  }
  
  const storedVersion = getStoredVersion()
  
  console.log('Version check:', { storedVersion, serverVersion, areDifferent: serverVersion !== storedVersion })
  
  // If versions are different, update is available
  if (serverVersion !== storedVersion) {
    return {
      available: true,
      currentVersion: storedVersion,
      newVersion: serverVersion
    }
  }
  
  return {
    available: false,
    currentVersion: storedVersion,
    newVersion: serverVersion
  }
}

/**
 * Initialize version check
 * @param {Function} onUpdateAvailable - Callback when update is detected
 */
export function initializeVersionCheck(onUpdateAvailable) {
  // Check immediately
  checkForUpdate().then(result => {
    if (result.available) {
      onUpdateAvailable(result)
    }
  })
  
  // Check periodically
  versionCheckInterval = setInterval(async () => {
    const result = await checkForUpdate()
    if (result.available) {
      onUpdateAvailable(result)
    }
  }, VERSION_CHECK_INTERVAL)
  
  return () => {
    if (versionCheckInterval) {
      clearInterval(versionCheckInterval)
      versionCheckInterval = null
    }
  }
}

/**
 * Update stored version to match server version
 */
export async function updateStoredVersion() {
  const serverVersion = await fetchServerVersion()
  if (serverVersion) {
    storeVersion(serverVersion)
    return serverVersion
  }
  return null
}

/**
 * Clear version check interval
 */
export function stopVersionCheck() {
  if (versionCheckInterval) {
    clearInterval(versionCheckInterval)
    versionCheckInterval = null
  }
}

/**
 * Quick check for update on page load
 * Returns true if update is available
 */
export async function quickCheckForUpdate() {
  try {
    const result = await checkForUpdate()
    return result.available
  } catch (e) {
    console.error('Error in quick version check:', e)
    return false
  }
}

